// Optional background image for pedigree pages (a loft photo, crest or paper
// texture behind the whole A4 sheet).
//
// Every pedigree draws its text straight onto the page — the boxes have no
// opaque fill — so a raw photo behind them would make the pedigree unreadable.
// The background is therefore ALWAYS shown under a white wash: the stored
// image is never drawn as-is. `fade` is the wash's opacity in percent, and it
// is clamped to at least MIN_FADE. That floor is not cosmetic: white-over-src
// compositing turns the darkest possible pixel (0) into 255 * fade, and at 50%
// that is mid-grey (128), which still gives black text a 5.3:1 contrast ratio
// — above the 4.5:1 needed for small text. Any image, any fade the UI allows,
// stays legible.
//
// The same wash is produced two ways, deliberately:
//   - on screen, by layering `overlayColor(fade)` over the image in CSS;
//   - in the PDF, by baking the wash into a flattened JPEG (renderForPdf).
// Both compute src * (1 - a) + 255 * a, so the preview matches the print. The
// PDF bakes rather than using PDF transparency so that every viewer and
// printer renders the same washed-out image.

import { dataUrlBytes, ensureDataUrl, getImageDimensions, processImageFile } from './imageUtils';

// Settings keys (also read by the PDF generator and the web worker).
export const BACKGROUND_KEY = 'pedigreeBackground';
export const BACKGROUND_FADE_KEY = 'pedigreeBackgroundFade';

export const MIN_FADE = 50;
export const MAX_FADE = 95;
export const DEFAULT_FADE = 75;

// A4 portrait, matching TraditionalPdfGenerator's page size.
export const PAGE_ASPECT = 210 / 297;
// ~150 dpi down the long edge of an A4 page: plenty for a faded backdrop,
// and it keeps the extra weight in every exported PDF modest.
const PDF_MAX_HEIGHT = 1754;

// Source images are stored once and re-cropped per use, so keep a little more
// detail than a bird photo — a landscape shot loses most of its width to the
// portrait crop.
const SOURCE_MAX_DIMENSION = 1600;
const SOURCE_QUALITY = 0.75;
const BAKED_QUALITY = 0.72;
/** Largest background we will store (fits comfortably under the 3 MB the
 *  web worker accepts for an uploaded image). */
export const MAX_BACKGROUND_BYTES = 900 * 1024;

/** Clamp a stored/typed fade to the legible range; anything unusable -> default.
 *  Note the empty checks: a setting that was never saved reads back as null
 *  (desktop) or undefined (web), and Number('') / Number(null) are 0 — which
 *  would silently pin an unconfigured loft to the faintest legal overlay. */
export function clampFade(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return DEFAULT_FADE;
  }
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return DEFAULT_FADE;
  return Math.min(MAX_FADE, Math.max(MIN_FADE, n));
}

/** The white wash as a CSS color — used for on-screen previews. */
export function overlayColor(fade) {
  return `rgba(255, 255, 255, ${clampFade(fade) / 100})`;
}

/**
 * Whether a stored value is one of the two shapes we ever write: an inline
 * image (desktop) or this account's object-storage URL (web app).
 *
 * Settings are restored verbatim from whatever backup file the user picks, so
 * this value is not necessarily ours. Anything else is ignored rather than
 * interpolated into a CSS url() or handed to fetch() — a hostile backup should
 * not be able to point a loft's pedigrees at a stranger's server.
 */
export function isSafeBackgroundSrc(src) {
  if (typeof src !== 'string' || !src) return false;
  return /^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/=\s]+$/.test(src) ||
    /^\/api\/photos\/[\w./-]+$/.test(src);
}

/**
 * CSS for an element that should show the washed background. The gradient is
 * a flat white layer painted OVER the image (CSS paints the first background
 * layer on top), which is the same compositing the PDF bakes in.
 * Returns null when no background is configured, so callers can spread it
 * unconditionally.
 */
export function backgroundStyles(src, fade) {
  if (!isSafeBackgroundSrc(src)) return null;
  const wash = overlayColor(fade);
  return {
    backgroundImage: `linear-gradient(${wash}, ${wash}), url("${src}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };
}

/**
 * Centre-crop rectangle that makes a srcW x srcH image fill a box of the
 * given aspect ratio (width / height) without distortion — the "cover" fit.
 * @returns {{sx: number, sy: number, sw: number, sh: number}} source rect
 */
export function coverCrop(srcW, srcH, aspect) {
  if (!(srcW > 0) || !(srcH > 0) || !(aspect > 0)) {
    return { sx: 0, sy: 0, sw: 0, sh: 0 };
  }
  if (srcW / srcH > aspect) {
    // Source is wider than the box: keep full height, trim the sides.
    const sw = srcH * aspect;
    return { sx: (srcW - sw) / 2, sy: 0, sw, sh: srcH };
  }
  const sh = srcW / aspect;
  return { sx: 0, sy: (srcH - sh) / 2, sw: srcW, sh };
}

/**
 * Read a chosen file into a stored-ready background image (downscaled JPEG
 * data URL). Throws a user-facing Error when the file is unusable or stays
 * too large even after re-compression.
 * @param {File} file
 * @returns {Promise<string>} data URL
 */
export async function processBackgroundFile(file) {
  const dataUrl = await processImageFile(file, {
    maxDimension: SOURCE_MAX_DIMENSION,
    quality: SOURCE_QUALITY,
    maxBytes: MAX_BACKGROUND_BYTES
  });
  if (dataUrlBytes(dataUrl) > MAX_BACKGROUND_BYTES) {
    throw new Error('That image is too detailed to use as a background. Please try a smaller or simpler one.');
  }
  return dataUrl;
}

// One-entry memo: exporting several pedigrees in a row re-bakes the same
// image otherwise, and the bake costs a full-page decode + JPEG encode.
let bakedCache = null; // { src, fade, out }

/**
 * Bake the background for the PDF: resolve the source (desktop stores a data
 * URL, the web app a /api/photos/ URL), centre-crop it to the A4 page, paint
 * the white wash over it, and flatten the result to a JPEG data URL.
 * @param {string} src stored background value
 * @param {number|string} fade wash strength in percent
 * @returns {Promise<string|null>} data URL, or null when there is nothing to draw
 */
export async function renderForPdf(src, fade) {
  if (!isSafeBackgroundSrc(src)) return null;
  const wash = clampFade(fade);
  if (bakedCache && bakedCache.src === src && bakedCache.fade === wash) {
    return bakedCache.out;
  }

  const dataUrl = await ensureDataUrl(src);
  if (!dataUrl) return null;
  const { width, height } = await getImageDimensions(dataUrl);
  const crop = coverCrop(width, height, PAGE_ASPECT);
  if (!(crop.sw > 0) || !(crop.sh > 0)) return null;

  const outH = Math.max(1, Math.round(Math.min(PDF_MAX_HEIGHT, crop.sh)));
  const outW = Math.max(1, Math.round(outH * PAGE_ASPECT));

  const image = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // White first: a source with transparency (or a crop that rounds a pixel
  // short) must never leave black showing through the flattened JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(image, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, outW, outH);
  ctx.fillStyle = overlayColor(wash);
  ctx.fillRect(0, 0, outW, outH);

  const out = canvas.toDataURL('image/jpeg', BAKED_QUALITY);
  bakedCache = { src, fade: wash, out };
  return out;
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Invalid image data'));
    img.src = dataUrl;
  });
}
